import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Alert,
  Autocomplete,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  PersonAdd as PersonAddIcon,
  Email as EmailIcon,
  People as PeopleIcon,
  LockReset as LockResetIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { da } from 'date-fns/locale';
import { adminApi } from '../../api/client';
import { Organization, OrgStatus } from '../../types';

interface CvrCompany {
  cvr: string;
  name: string;
  address: string;
  city: string;
  zipcode: string;
  phone?: string;
  email?: string;
}

const statusColors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  PENDING: 'warning',
  ACTIVE: 'success',
  TRIAL: 'info',
  EXPIRED: 'error',
  SUSPENDED: 'default',
};

const statusLabels: Record<string, string> = {
  PENDING: 'Afventer',
  ACTIVE: 'Aktiv',
  TRIAL: 'Prøveperiode',
  EXPIRED: 'Udløbet',
  SUSPENDED: 'Suspenderet',
};

export const OrganizationsList: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [trialEndDate, setTrialEndDate] = useState<Date | null>(null);
  const [newStatus, setNewStatus] = useState<OrgStatus>('ACTIVE');

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [cvrSearch, setCvrSearch] = useState('');
  const [cvrOptions, setCvrOptions] = useState<CvrCompany[]>([]);
  const [cvrLoading, setCvrLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<CvrCompany | null>(null);
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgCvr, setNewOrgCvr] = useState('');
  const [newOrgEmail, setNewOrgEmail] = useState('');
  const [newOrgPhone, setNewOrgPhone] = useState('');
  const [newOrgAddress, setNewOrgAddress] = useState('');
  const [newOrgCity, setNewOrgCity] = useState('');
  const [newOrgPostalCode, setNewOrgPostalCode] = useState('');
  const [seedDemoData, setSeedDemoData] = useState(false);

  // Admin user state
  const [adminEmail, setAdminEmail] = useState('');
  const [adminFirstName, setAdminFirstName] = useState('');
  const [adminLastName, setAdminLastName] = useState('');
  const [adminPhone, setAdminPhone] = useState('');

  // Success dialog state
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [createdResult, setCreatedResult] = useState<{ userId: string; email: string; password: string } | null>(null);
  const [invitationSent, setInvitationSent] = useState(false);
  const [invitationLoading, setInvitationLoading] = useState(false);

  // Users overview dialog state
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [usersDialogOrg, setUsersDialogOrg] = useState<Organization | null>(null);
  const [resetPasswordResult, setResetPasswordResult] = useState<{ userId: string; email: string; password: string } | null>(null);

  // Create user dialog state
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserFirstName, setNewUserFirstName] = useState('');
  const [newUserLastName, setNewUserLastName] = useState('');
  const [newUserPhone, setNewUserPhone] = useState('');
  const [newUserRole, setNewUserRole] = useState<'ADMIN' | 'MANAGER' | 'DISPATCHER' | 'VIEWER'>('DISPATCHER');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'organizations', { page, rowsPerPage, search, statusFilter }],
    queryFn: async () => {
      const res = await adminApi.getOrganizations({
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined,
        status: statusFilter || undefined,
      });
      return res.data;
    },
  });

  const { data: orgDetail, isLoading: orgDetailLoading } = useQuery({
    queryKey: ['admin', 'organization', usersDialogOrg?.id],
    queryFn: async () => {
      const res = await adminApi.getOrganization(usersDialogOrg!.id);
      return res.data.data;
    },
    enabled: !!usersDialogOrg && usersDialogOpen,
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (userId: string) => adminApi.resetUserPassword(userId),
    onSuccess: (response) => {
      const result = response.data.data;
      setResetPasswordResult({
        userId: result.userId,
        email: result.email,
        password: result.generatedPassword,
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      adminApi.updateOrganization(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] });
      setEditDialogOpen(false);
      setSelectedOrg(null);
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => adminApi.createOrganization(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] });
      const result = response.data.data;
      setCreatedResult({
        userId: result.user.id,
        email: result.user.email,
        password: result.generatedPassword,
      });
      setInvitationSent(false);
      handleCreateDialogClose();
      setSuccessDialogOpen(true);
    },
  });

  const createUserMutation = useMutation({
    mutationFn: ({ organizationId, data }: { organizationId: string; data: any }) =>
      adminApi.createUserForOrganization(organizationId, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] });
      const result = response.data.data;
      setCreatedResult({
        userId: result.user.id,
        email: result.user.email,
        password: result.generatedPassword,
      });
      setInvitationSent(false);
      handleCreateUserDialogClose();
      setSuccessDialogOpen(true);
    },
  });

  // CVR search directly from browser (avoids EC2 IP quota limits on cvrapi.dk)
  const searchCvr = useCallback(async (query: string) => {
    if (query.length < 2) {
      setCvrOptions([]);
      return;
    }
    setCvrLoading(true);
    try {
      // Detect if query is a CVR number (all digits) or a name
      const isNumber = /^\d+$/.test(query.trim());
      const param = isNumber ? `vat=${encodeURIComponent(query.trim())}` : `search=${encodeURIComponent(query)}`;
      const res = await fetch(`https://cvrapi.dk/api?${param}&country=dk`, {
        headers: { 'User-Agent': 'E3-CRM/1.0' },
      });
      if (!res.ok) {
        setCvrOptions([]);
        return;
      }
      const data = await res.json();
      // cvrapi.dk returns single object for exact CVR, array for name search
      if (Array.isArray(data)) {
        setCvrOptions(data.map((d: any) => ({
          cvr: String(d.vat || ''),
          name: d.name || '',
          address: d.address || '',
          city: d.city || '',
          zipcode: d.zipcode || '',
          phone: d.phone || undefined,
          email: d.email || undefined,
        })));
      } else if (data.vat) {
        setCvrOptions([{
          cvr: String(data.vat),
          name: data.name || '',
          address: data.address || '',
          city: data.city || '',
          zipcode: data.zipcode || '',
          phone: data.phone || undefined,
          email: data.email || undefined,
        }]);
      } else {
        setCvrOptions([]);
      }
    } catch (err) {
      console.error('CVR search error:', err);
      setCvrOptions([]);
    } finally {
      setCvrLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (cvrSearch) {
        searchCvr(cvrSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [cvrSearch, searchCvr]);

  const handleCreateDialogClose = () => {
    setCreateDialogOpen(false);
    setCvrSearch('');
    setCvrOptions([]);
    setSelectedCompany(null);
    setNewOrgName('');
    setNewOrgCvr('');
    setNewOrgEmail('');
    setNewOrgPhone('');
    setNewOrgAddress('');
    setNewOrgCity('');
    setNewOrgPostalCode('');
    setSeedDemoData(false);
    setAdminEmail('');
    setAdminFirstName('');
    setAdminLastName('');
    setAdminPhone('');
  };

  const handleCompanySelect = (company: CvrCompany | null) => {
    setSelectedCompany(company);
    if (company) {
      setNewOrgName(company.name);
      setNewOrgCvr(company.cvr);
      setNewOrgEmail(company.email || '');
      setNewOrgPhone(company.phone || '');
      setNewOrgAddress(company.address);
      setNewOrgCity(company.city);
      setNewOrgPostalCode(company.zipcode);
    }
  };

  const handleCreateOrganization = () => {
    createMutation.mutate({
      name: newOrgName,
      cvr: newOrgCvr || undefined,
      email: newOrgEmail || undefined,
      phone: newOrgPhone || undefined,
      address: newOrgAddress || undefined,
      city: newOrgCity || undefined,
      postalCode: newOrgPostalCode || undefined,
      seedDemoData,
      adminEmail,
      adminFirstName: adminFirstName || undefined,
      adminLastName: adminLastName || undefined,
      adminPhone: adminPhone || undefined,
    });
  };

  const handleCreateUserDialogClose = () => {
    setCreateUserDialogOpen(false);
    setNewUserEmail('');
    setNewUserFirstName('');
    setNewUserLastName('');
    setNewUserPhone('');
    setNewUserRole('DISPATCHER');
  };

  const handleOpenCreateUserDialog = () => {
    handleMenuClose();
    setCreateUserDialogOpen(true);
  };

  const handleCreateUser = () => {
    if (!selectedOrg) return;
    createUserMutation.mutate({
      organizationId: selectedOrg.id,
      data: {
        email: newUserEmail,
        firstName: newUserFirstName || undefined,
        lastName: newUserLastName || undefined,
        phone: newUserPhone || undefined,
        role: newUserRole,
      },
    });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, org: Organization) => {
    setAnchorEl(event.currentTarget);
    setSelectedOrg(org);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditOpen = () => {
    if (selectedOrg) {
      setNewStatus(selectedOrg.status);
      setTrialEndDate(selectedOrg.trialEndDate ? new Date(selectedOrg.trialEndDate) : null);
      setEditDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleUpdateOrg = () => {
    if (selectedOrg) {
      updateMutation.mutate({
        id: selectedOrg.id,
        data: {
          status: newStatus,
          trialEndDate: trialEndDate?.toISOString(),
        },
      });
    }
  };

  const handleSuspend = () => {
    if (selectedOrg) {
      updateMutation.mutate({
        id: selectedOrg.id,
        data: { status: 'SUSPENDED' },
      });
    }
    handleMenuClose();
  };

  const handleActivate = () => {
    if (selectedOrg) {
      updateMutation.mutate({
        id: selectedOrg.id,
        data: { status: 'ACTIVE' },
      });
    }
    handleMenuClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={da}>
      <Box>
        <Typography variant="h5" fontWeight={600} mb={3}>
          Organisationer
        </Typography>

        {/* Filters */}
        <Card sx={{ borderRadius: 2, mb: 3 }}>
          <CardContent>
            <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
              <TextField
                size="small"
                placeholder="Søg organisation..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 250 }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">Alle</MenuItem>
                  <MenuItem value="PENDING">Afventer</MenuItem>
                  <MenuItem value="ACTIVE">Aktiv</MenuItem>
                  <MenuItem value="TRIAL">Prøveperiode</MenuItem>
                  <MenuItem value="EXPIRED">Udløbet</MenuItem>
                  <MenuItem value="SUSPENDED">Suspenderet</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ flex: 1 }} />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
              >
                Opret organisation
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Table */}
        <Card sx={{ borderRadius: 2 }}>
          {error && (
            <Alert severity="error" sx={{ m: 2 }}>
              Kunne ikke hente organisationer
            </Alert>
          )}

          {isLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Organisation</TableCell>
                      <TableCell>CVR</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Prøveperiode slut</TableCell>
                      <TableCell>Brugere</TableCell>
                      <TableCell>Oprettet</TableCell>
                      <TableCell align="right">Handlinger</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data?.data?.map((org: Organization) => (
                      <TableRow key={org.id} hover>
                        <TableCell>
                          <Typography fontWeight={500}>{org.name}</Typography>
                        </TableCell>
                        <TableCell>{org.cvr || '-'}</TableCell>
                        <TableCell>{org.email}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={statusLabels[org.status] || org.status}
                            color={statusColors[org.status] || 'default'}
                            sx={{ borderRadius: 1 }}
                          />
                        </TableCell>
                        <TableCell>
                          {org.trialEndDate
                            ? new Date(org.trialEndDate).toLocaleDateString('da-DK')
                            : '-'}
                        </TableCell>
                        <TableCell>{org._count?.users || 0}</TableCell>
                        <TableCell>
                          {new Date(org.createdAt).toLocaleDateString('da-DK')}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, org)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={data?.pagination?.total || 0}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Rækker pr. side:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} af ${count}`
                }
              />
            </>
          )}
        </Card>

        {/* Context Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => {
            setUsersDialogOrg(selectedOrg);
            setUsersDialogOpen(true);
            setResetPasswordResult(null);
            handleMenuClose();
          }}>
            <PeopleIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
            Vis brugere
          </MenuItem>
          <MenuItem onClick={handleEditOpen}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Rediger
          </MenuItem>
          <MenuItem onClick={handleOpenCreateUserDialog}>
            <PersonAddIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
            Opret bruger
          </MenuItem>
          {selectedOrg?.status === 'SUSPENDED' ? (
            <MenuItem onClick={handleActivate}>
              <CheckCircleIcon fontSize="small" sx={{ mr: 1, color: 'success.main' }} />
              Aktiver
            </MenuItem>
          ) : (
            <MenuItem onClick={handleSuspend}>
              <BlockIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
              Suspender
            </MenuItem>
          )}
        </Menu>

        {/* Edit Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Rediger organisation</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {selectedOrg?.name}
              </Typography>

              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={newStatus}
                  label="Status"
                  onChange={(e) => setNewStatus(e.target.value as OrgStatus)}
                >
                  <MenuItem value="PENDING">Afventer</MenuItem>
                  <MenuItem value="ACTIVE">Aktiv</MenuItem>
                  <MenuItem value="TRIAL">Prøveperiode</MenuItem>
                  <MenuItem value="EXPIRED">Udløbet</MenuItem>
                  <MenuItem value="SUSPENDED">Suspenderet</MenuItem>
                </Select>
              </FormControl>

              <DatePicker
                label="Prøveperiode slutter"
                value={trialEndDate}
                onChange={(date) => setTrialEndDate(date)}
                slotProps={{
                  textField: { size: 'small', fullWidth: true },
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Annuller</Button>
            <Button
              variant="contained"
              onClick={handleUpdateOrg}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Gemmer...' : 'Gem'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create Dialog */}
        <Dialog
          open={createDialogOpen}
          onClose={handleCreateDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Opret organisation</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Autocomplete
                freeSolo
                options={cvrOptions}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : `${option.name} (CVR: ${option.cvr})`
                }
                filterOptions={(x) => x}
                loading={cvrLoading}
                inputValue={cvrSearch}
                onInputChange={(_, value) => {
                  setCvrSearch(value);
                  if (!selectedCompany) {
                    setNewOrgName(value);
                  }
                }}
                onChange={(_, value) => {
                  if (value && typeof value !== 'string') {
                    handleCompanySelect(value);
                  }
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.cvr}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        CVR: {option.cvr} | {option.address}, {option.zipcode} {option.city}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Søg virksomhed (CVR)"
                    size="small"
                    placeholder="Indtast firmanavn eller CVR..."
                    helperText="Søg i CVR-registret for at auto-udfylde"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {cvrLoading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <TextField
                size="small"
                label="Navn"
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                required
              />

              <TextField
                size="small"
                label="CVR"
                value={newOrgCvr}
                onChange={(e) => setNewOrgCvr(e.target.value)}
              />

              <TextField
                size="small"
                label="Email"
                value={newOrgEmail}
                onChange={(e) => setNewOrgEmail(e.target.value)}
              />

              <TextField
                size="small"
                label="Telefon"
                value={newOrgPhone}
                onChange={(e) => setNewOrgPhone(e.target.value)}
              />

              <TextField
                size="small"
                label="Adresse"
                value={newOrgAddress}
                onChange={(e) => setNewOrgAddress(e.target.value)}
              />

              <Box display="flex" gap={2}>
                <TextField
                  size="small"
                  label="Postnummer"
                  value={newOrgPostalCode}
                  onChange={(e) => setNewOrgPostalCode(e.target.value)}
                  sx={{ width: 120 }}
                />
                <TextField
                  size="small"
                  label="By"
                  value={newOrgCity}
                  onChange={(e) => setNewOrgCity(e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Box>

              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                Admin-bruger
              </Typography>

              <TextField
                size="small"
                label="Admin email *"
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                required
                helperText="Email til første bruger (admin)"
              />

              <Box display="flex" gap={2}>
                <TextField
                  size="small"
                  label="Fornavn"
                  value={adminFirstName}
                  onChange={(e) => setAdminFirstName(e.target.value)}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label="Efternavn"
                  value={adminLastName}
                  onChange={(e) => setAdminLastName(e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Box>

              <TextField
                size="small"
                label="Admin telefon"
                value={adminPhone}
                onChange={(e) => setAdminPhone(e.target.value)}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={seedDemoData}
                    onChange={(e) => setSeedDemoData(e.target.checked)}
                  />
                }
                label="Tilføj demo-data (kunder, segmenter)"
              />

              {createMutation.isError && (
                <Alert severity="error">
                  {(createMutation.error as any)?.response?.data?.error || 'Kunne ikke oprette organisation'}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCreateDialogClose}>Annuller</Button>
            <Button
              variant="contained"
              onClick={handleCreateOrganization}
              disabled={createMutation.isPending || !newOrgName || !adminEmail}
            >
              {createMutation.isPending ? 'Opretter...' : 'Opret'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create User Dialog */}
        <Dialog
          open={createUserDialogOpen}
          onClose={handleCreateUserDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Opret bruger til {selectedOrg?.name}</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                size="small"
                label="Email *"
                type="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                required
              />

              <Box display="flex" gap={2}>
                <TextField
                  size="small"
                  label="Fornavn"
                  value={newUserFirstName}
                  onChange={(e) => setNewUserFirstName(e.target.value)}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label="Efternavn"
                  value={newUserLastName}
                  onChange={(e) => setNewUserLastName(e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Box>

              <TextField
                size="small"
                label="Telefon"
                value={newUserPhone}
                onChange={(e) => setNewUserPhone(e.target.value)}
              />

              <FormControl fullWidth size="small">
                <InputLabel>Rolle</InputLabel>
                <Select
                  value={newUserRole}
                  label="Rolle"
                  onChange={(e) => setNewUserRole(e.target.value as 'ADMIN' | 'MANAGER' | 'DISPATCHER' | 'VIEWER')}
                >
                  <MenuItem value="ADMIN">Admin</MenuItem>
                  <MenuItem value="MANAGER">Manager</MenuItem>
                  <MenuItem value="DISPATCHER">Dispatcher</MenuItem>
                  <MenuItem value="VIEWER">Viewer (kun læseadgang)</MenuItem>
                </Select>
              </FormControl>

              {createUserMutation.isError && (
                <Alert severity="error">
                  {(createUserMutation.error as any)?.response?.data?.error || 'Kunne ikke oprette bruger'}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCreateUserDialogClose}>Annuller</Button>
            <Button
              variant="contained"
              onClick={handleCreateUser}
              disabled={createUserMutation.isPending || !newUserEmail}
            >
              {createUserMutation.isPending ? 'Opretter...' : 'Opret'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Users Overview Dialog */}
        <Dialog
          open={usersDialogOpen}
          onClose={() => {
            setUsersDialogOpen(false);
            setUsersDialogOrg(null);
            setResetPasswordResult(null);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Brugere - {usersDialogOrg?.name}
          </DialogTitle>
          <DialogContent>
            {orgDetailLoading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {resetPasswordResult && (
                  <Alert severity="info" sx={{ mb: 2 }} onClose={() => setResetPasswordResult(null)}>
                    Nyt kodeord til <strong>{resetPasswordResult.email}</strong>:{' '}
                    <code style={{ backgroundColor: '#fff', padding: '2px 6px', borderRadius: 4 }}>
                      {resetPasswordResult.password}
                    </code>
                    <IconButton
                      size="small"
                      sx={{ ml: 1 }}
                      onClick={() => navigator.clipboard.writeText(resetPasswordResult.password)}
                    >
                      <Typography variant="caption">Kopier</Typography>
                    </IconButton>
                  </Alert>
                )}
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Navn</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Rolle</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Seneste login</TableCell>
                        <TableCell>Oprettet</TableCell>
                        <TableCell align="right">Handlinger</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orgDetail?.users?.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography variant="body2" color="text.secondary" py={2}>
                              Ingen brugere endnu
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {orgDetail?.users?.map((user: any) => (
                        <TableRow key={user.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {[user.firstName, user.lastName].filter(Boolean).join(' ') || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={user.role}
                              color={user.role === 'ADMIN' ? 'primary' : 'default'}
                              sx={{ borderRadius: 1 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={user.isActive ? 'Aktiv' : 'Inaktiv'}
                              color={user.isActive ? 'success' : 'default'}
                              sx={{ borderRadius: 1 }}
                            />
                          </TableCell>
                          <TableCell>
                            {user.lastLoginAt
                              ? new Date(user.lastLoginAt).toLocaleDateString('da-DK', {
                                  day: 'numeric', month: 'short', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit',
                                })
                              : 'Aldrig'}
                          </TableCell>
                          <TableCell>
                            {new Date(user.createdAt).toLocaleDateString('da-DK')}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              title="Nulstil kodeord"
                              onClick={() => resetPasswordMutation.mutate(user.id)}
                              disabled={resetPasswordMutation.isPending}
                            >
                              <LockResetIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              title="Send invitation email"
                              onClick={async () => {
                                try {
                                  await adminApi.sendSetupEmail(user.id);
                                  alert(`Invitation sendt til ${user.email}`);
                                } catch (err) {
                                  console.error('Failed to send invitation:', err);
                                  alert('Kunne ikke sende invitation');
                                }
                              }}
                            >
                              <EmailIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              startIcon={<PersonAddIcon />}
              onClick={() => {
                setUsersDialogOpen(false);
                setCreateUserDialogOpen(true);
              }}
            >
              Opret bruger
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setUsersDialogOpen(false);
                setUsersDialogOrg(null);
                setResetPasswordResult(null);
              }}
            >
              Luk
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success Dialog with Password */}
        <Dialog
          open={successDialogOpen}
          onClose={() => setSuccessDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
            Bruger oprettet!
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert severity="success">
                Brugeren er nu oprettet.
              </Alert>

              <Typography variant="body2" color="text.secondary">
                Send følgende loginoplysninger til brugeren:
              </Typography>

              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Email:</strong> {createdResult?.email}
                </Typography>
                <Typography variant="body2">
                  <strong>Kodeord:</strong>{' '}
                  <code style={{
                    backgroundColor: '#fff',
                    padding: '2px 8px',
                    borderRadius: 4,
                    fontFamily: 'monospace',
                    fontSize: '1.1em'
                  }}>
                    {createdResult?.password}
                  </code>
                </Typography>
              </Box>

              <Alert severity="warning" sx={{ fontSize: '0.85rem' }}>
                Kopier kodeordet nu - det kan ikke vises igen!
              </Alert>

              {invitationSent && (
                <Alert severity="success" sx={{ fontSize: '0.85rem' }}>
                  Invitation sendt til {createdResult?.email}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              disabled={invitationLoading || invitationSent}
              onClick={async () => {
                if (!createdResult?.userId) return;
                setInvitationLoading(true);
                try {
                  await adminApi.sendSetupEmail(createdResult.userId);
                  setInvitationSent(true);
                } catch (err) {
                  console.error('Failed to send invitation:', err);
                } finally {
                  setInvitationLoading(false);
                }
              }}
            >
              {invitationLoading ? 'Sender...' : invitationSent ? 'Invitation sendt' : 'Send invitation email'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                navigator.clipboard.writeText(
                  `Email: ${createdResult?.email}\nKodeord: ${createdResult?.password}`
                );
              }}
            >
              Kopier til udklipsholder
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setSuccessDialogOpen(false);
                setCreatedResult(null);
                setInvitationSent(false);
              }}
            >
              Luk
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default OrganizationsList;
